import React from 'react';
import { Badge } from '@onward-delivery/design';
import { Pencil } from '@onward-delivery/design/icons';

import { CEILINGS } from '../constants';
import { TOKENS } from '../blocks';
import { formatTimeframe } from '../dayMetrics';
import {
    NOT_SHIPPER_BOOKABLE,
    NOT_SHIPPER_BOOKABLE_HINT,
    blocksEveryBooking,
    equipmentLabels,
    serviceTypeLabels,
    vehicleLabel,
    subregionNames,
    zeroCeilingHint,
} from './utils';

const dashed = (value) => (value === null || value === undefined || value === '' ? '-' : value);

// One line of chips that truncates, rather than a cell that grows with the list. The full set is
// on the resource's own dialog; this column answers "roughly what does it carry".
const ChipList = ({ values, empty }) => {
    if (!values.length) {
        return <span className="text-muted">{empty}</span>;
    }

    return (
        <div className="flex flex-wrap gap-1">
            {values.map((value) => (
                // Onward green, the same token the calendar's fill badges use. A neutral badge read
                // as disabled.
                <Badge key={value} appearance="tinted" color={TOKENS.primary} size="xs">
                    {value}
                </Badge>
            ))}
        </div>
    );
};

export const fleetColumns = ({ subregionLabels, onEdit }) => [
    { accessorKey: 'name', header: 'Resource', size: 180 },
    {
        id: 'vehicle_type',
        header: 'Vehicle',
        size: 140,
        accessorFn: (row) => vehicleLabel(row),
        cell: ({ getValue }) => dashed(getValue()),
        meta: { filter: { type: 'multi-select' } },
    },
    {
        id: 'box_size',
        header: 'Box Size',
        size: 140,
        accessorFn: (row) => row.box_size,
        cell: ({ getValue }) => dashed(getValue()),
    },
    // timeframe_start/end are whole hours bounding the delivery window offered to shippers, not
    // when the resource starts — that is start_time, in minutes past midnight. Off by default
    // because it is shipper-facing scheduling and reads like a shift time on a fleet screen.
    {
        id: 'timeframe',
        header: 'Hours',
        size: 130,
        accessorFn: (row) => formatTimeframe(row.timeframe_start, row.timeframe_end),
        cell: ({ getValue }) => dashed(getValue()),
    },
    // Each ceiling is its own column so they sort, and so "not set" is visible per dimension rather
    // than hidden inside a summary. A resource can be capped on weight and uncapped on cubes.
    ...CEILINGS.map(({ value, label, column, short }) => ({
        id: column,
        header: label,
        size: 120,
        accessorFn: (row) => row[column],
        cell: ({ getValue }) => {
            const limit = getValue();

            if (limit == null) {
                return <span className="text-muted">Not set</span>;
            }

            const text = `${Number(limit).toLocaleString()} ${short}`;

            // The dialog rejects these now, but rows saved before it did can still hold one.
            return blocksEveryBooking(limit) ? (
                <span className="text-destructive" title={zeroCeilingHint(value)}>
                    {text}
                </span>
            ) : (
                text
            );
        },
    })),
    {
        id: 'subregions',
        header: 'Subregions',
        size: 200,
        accessorFn: (row) => subregionNames(row, subregionLabels).join(', '),
        // No subregion is a valid setup (a fleet-only vehicle), so the empty cell is muted rather than
        // an error. It still says what it means: shippers cannot book the resource.
        cell: ({ row }) => {
            const names = subregionNames(row.original, subregionLabels);

            return names.length ? (
                names.join(', ')
            ) : (
                <span className="text-muted" title={NOT_SHIPPER_BOOKABLE_HINT}>
                    {NOT_SHIPPER_BOOKABLE}
                </span>
            );
        },
    },
    {
        id: 'equipment',
        header: 'Equipment',
        size: 220,
        enableSorting: false,
        cell: ({ row }) => <ChipList values={equipmentLabels(row.original)} empty="None" />,
    },
    {
        id: 'service_types',
        header: 'Service Levels',
        size: 220,
        enableSorting: false,
        // An empty array means the resource takes any service level, which is the opposite of
        // "nothing configured" — the cell has to say which.
        cell: ({ row }) => <ChipList values={serviceTypeLabels(row.original)} empty="Any" />,
    },
    {
        id: 'edit',
        header: '',
        size: 60,
        enableSorting: false,
        // The only way into a resource's configuration from this page. ColumnVisibilityMenu lists
        // whatever answers getCanHide(), so this keeps it off that list as well as locking it on.
        enableHiding: false,
        cell: ({ row }) => (
            <button
                type="button"
                aria-label={`Edit ${row.original.name}`}
                className="text-muted hover:text-foreground"
                onClick={() => onEdit(row.original)}
            >
                <Pencil className="h-4 w-4" />
            </button>
        ),
    },
];
